// Reproducible synthetic teaching data, not output from a real SOSSA run.
export function createSossaExample(seed = 162) {
  let state = seed >>> 0;
  const random = () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return (state + 1) / 4294967297;
  };
  const normal = () => Math.sqrt(-2 * Math.log(random())) * Math.cos(2 * Math.PI * random());
  const round = (value) => Math.round(value * 1000) / 1000;
  const sampleRateHz = 30000;
  const durationSamples = sampleRateHz * 60;
  const clusters = [];
  for (let channel = 1; channel <= 8; channel += 1) {
    const channelClusters = [];
    const spacing = channel % 2 ? 30 : 12;
    const centers = [0, 1, 2].map((unit) => 45 + channel * 4 + unit * spacing);
    for (let unit = 0; unit < 3; unit += 1) {
      const id = (channel - 1) * 3 + unit + 1;
      const count = 240 + ((id - 1) % 6) * 64;
      const scenario = unit === 2 ? 'bimodal' : unit === 1 ? 'amplitude drift' : 'stable';
      const times = Array.from({ length: count }, () => Math.floor(random() * durationSamples)).sort((a, b) => a - b);
      const amplitudes = [];
      const best = [];
      const second = [];
      const waveforms = times.map((time, spikeIndex) => {
        const progress = time / durationSamples;
        const drift = unit === 1 ? (progress - 0.5) * 22 : 0;
        const secondMode = unit === 2 && random() < 0.3 ? 24 : 0;
        const outlier = random() < 0.025 ? normal() * 18 : 0;
        const amplitude = Math.max(12, centers[unit] + normal() * (4 + channel % 3) + drift + secondMode + outlier);
        // Illustrative ambiguity increases when amplitude is close to a neighboring cluster.
        const otherDistance = Math.min(...centers.filter((_, index) => index !== unit).map((center) => Math.abs(amplitude - center)));
        const confidence = Math.max(0.02, Math.min(0.98, 0.15 + otherDistance / 45 + normal() * 0.1));
        const bestMatch = round(80 + random() * 60);
        const width = 3.2 + unit * 0.7 + channel * 0.08;
        const shift = normal() * 0.3;
        const shape = Array.from({ length: 48 }, (_, index) => {
          const x = index - 19 - shift;
          return -Math.exp(-((x / width) ** 2) / 2)
            + 0.48 * Math.exp(-(((x - 9) / (width * 1.5)) ** 2) / 2)
            + 0.14 * Math.exp(-(((x + 7) / 2.8) ** 2) / 2) + normal() * 0.012;
        });
        const range = Math.max(...shape) - Math.min(...shape);
        const values = shape.map((value) => round(value * amplitude / range));
        amplitudes.push(round(Math.max(...values) - Math.min(...values)));
        best.push(bestMatch);
        second.push(round(bestMatch * (1 - confidence)));
        const channels = [];
        for (let neighbor = Math.max(1, channel - 2); neighbor <= Math.min(8, channel + 2); neighbor += 1) {
          if (neighbor === channel) continue;
          const distance = Math.abs(neighbor - channel);
          const attenuation = Math.exp(-distance * 0.65);
          const delay = neighbor < channel ? -1 : 1;
          channels.push({ channel: neighbor, amplitude: values.map((_, index) => round(
            values[Math.max(0, Math.min(47, index - delay))] * attenuation
            + normal() * amplitude * 0.008
          )) });
        }
        return { spikeIndex, time, channel, amplitude: values, channels,
          timePoints: Array.from({ length: 48 }, (_, index) => index - 19) };
      });
      channelClusters.push({ id, primaryChannel: channel, label: `CH ${channel} · ${scenario}`,
        metadata: { scenario, synthetic: true }, spikeTimes: times,
        spikeChannels: times.map(() => channel), spikeAmplitudes: amplitudes,
        best_match: best, second_match: second, waveforms });
    }
    const fsCount = Math.ceil(channelClusters.reduce((sum, cluster) => sum + cluster.spikeTimes.length, 0) / (0.76 + channel * 0.02));
    channelClusters.forEach((cluster) => { cluster.num_fs_spikes = fsCount; clusters.push(cluster); });
  }
  return { algorithmName: 'SOSSA', metadata: { example: true, synthetic: true, seed,
    description: '24 clusters on 8 channels: stable, drifting, bimodal and overlapping amplitude profiles',
    timeUnit: 'samples', sampleRateHz, durationSamples, primaryChannelsProvided: true }, clusters };
}

export default createSossaExample;
